import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import type { PcmpRequestBatchesParams } from '../types/pcmpRequest.types';
import {
  PCMP_REQUESTED_ALL_TAB,
  PCMP_REQUESTED_BATCHES_TAB,
  type PcmpRequestedParams,
  type PcmpRequestedSearch,
} from '../types/pcmpRequested.types';
import { usePcmpQueueFilters } from './usePcmpQueueFilters';

const route = getRouteApi('/_auth/pcmp/requests/requested/');

/** The Requested Proposals URL state: the tab (a key from the counts), filters and paging. */
export function usePcmpRequestedFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const setSearch = useCallback(
    (update: (prev: PcmpRequestedSearch) => PcmpRequestedSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );

  const queue = usePcmpQueueFilters(search, setSearch);
  const { appliedFilters, page, limit } = queue;

  const tab = search.tab ?? PCMP_REQUESTED_ALL_TAB;
  const isBatchesTab = tab === PCMP_REQUESTED_BATCHES_TAB;

  /** The request list's params; null on the Batches tab. */
  const listParams = useMemo<PcmpRequestedParams | null>(
    () => (isBatchesTab ? null : { ...appliedFilters, tab, page, limit }),
    [appliedFilters, tab, page, limit, isBatchesTab],
  );

  const batchesParams = useMemo<PcmpRequestBatchesParams>(
    () => ({ ...appliedFilters, page, limit }),
    [appliedFilters, page, limit],
  );

  const setTab = useCallback(
    (next: string) => {
      setSearch((prev) => ({
        ...prev,
        tab: next === PCMP_REQUESTED_ALL_TAB ? undefined : next,
        page: undefined,
      }));
    },
    [setSearch],
  );

  return { ...queue, tab, isBatchesTab, listParams, batchesParams, setTab };
}

export type PcmpRequestedFilterController = ReturnType<typeof usePcmpRequestedFilters>;
